import {
  GamoraBase64URLGuard,
  GamoraControllerContextIdentifier,
  GamoraErrorUnhandledException,
  GamoraFoundationGuard,
  GamoraUUIDGuard,
  assert,
  convertEnumToValues,
} from '@ver.id/libs.internal.gamora';
import { HoruzProtocolChulakDataType } from '../data.js';
import { HoruzProtocolChulakLastState } from '../states.js';
import { HoruzProtocolChulakServiceStateYielder } from '../states/service/enums.js';

/**
 * @public
 */
export type HoruzProtocolChulakServiceSessionGuardDataElement = [
  HoruzProtocolChulakDataType,
  number[],
];

/**
 * @public
 */
export enum HoruzProtocolChulakServiceSessionGuardTagsType {
  Qr = 'Qr',
  Redirect = 'Redirect',
  Dynamic = 'Dynamic',
}

/**
 * Input type
 * @public
 */
export interface HoruzProtocolChulakServiceSessionGuardTagsQR {
  content: string;
}

/**
 * Input type
 * @public
 */
export interface HoruzProtocolChulakServiceSessionGuardTagsRedirect {
  uri: string;
}

/**
 * Input type
 * @public
 */
export interface HoruzProtocolChulakServiceSessionGuardTagsApp {
  continueInApp?: boolean;
  deepAppLink?: string;
  intentAppLink?: string;
  universalAppLink?: string;
}

/**
 * Input type
 * @public
 */
export interface HoruzProtocolChulakServiceSessionGuardTagsDynamic {
  frequency: number;
  tags: Exclude<
    HoruzProtocolChulakServiceSessionGuardTags,
    { type: HoruzProtocolChulakServiceSessionGuardTagsType.Dynamic }
  >;
}

/**
 * Input type
 * @public
 */
export type HoruzProtocolChulakServiceSessionGuardTags =
  | {
      type: HoruzProtocolChulakServiceSessionGuardTagsType.Qr;
      qr: HoruzProtocolChulakServiceSessionGuardTagsQR;
      app?: HoruzProtocolChulakServiceSessionGuardTagsApp;
      [key: string]: unknown;
    }
  | {
      type: HoruzProtocolChulakServiceSessionGuardTagsType.Redirect;
      redirect: HoruzProtocolChulakServiceSessionGuardTagsRedirect;
      app?: HoruzProtocolChulakServiceSessionGuardTagsApp;
      [key: string]: unknown;
    }
  | {
      type: HoruzProtocolChulakServiceSessionGuardTagsType.Dynamic;
      dynamic: HoruzProtocolChulakServiceSessionGuardTagsDynamic;
      app?: HoruzProtocolChulakServiceSessionGuardTagsApp;
      [key: string]: unknown;
    };

/**
 * Input type
 * @public
 */
export interface HoruzProtocolChulakServiceSessionGuardInput {
  uuid: string;
  exp: number;
  data: HoruzProtocolChulakServiceSessionGuardDataElement[];
  lastState: HoruzProtocolChulakLastState | null;
  yielded: HoruzProtocolChulakServiceStateYielder[];
  tags: HoruzProtocolChulakServiceSessionGuardTags | null;
  contextIdentifiers: string[];
}

/**
 * @public
 */
export class HoruzProtocolChulakServiceSessionGuard extends GamoraFoundationGuard<HoruzProtocolChulakServiceSessionGuardInput> {
  protected _uuid: GamoraUUIDGuard = GamoraUUIDGuard.Empty();
  protected _exp = 0;
  protected _data: Map<HoruzProtocolChulakDataType, Uint8Array> = new Map();
  protected _lastState: HoruzProtocolChulakLastState | null = null;
  protected _yielded: Set<HoruzProtocolChulakServiceStateYielder> = new Set();
  protected _tags: HoruzProtocolChulakServiceSessionGuardTags | null = null;
  protected _contextIdentifiers: string[] = [];

  /**
   * Constructor
   * @param input - the input
   */
  constructor(input: HoruzProtocolChulakServiceSessionGuardInput) {
    super();
    assert(
      typeof input.data === 'object' && input.data !== null,
      'Invalid input argument: not a valid object.',
    );

    // Identifier
    this._uuid = GamoraUUIDGuard.fromJSON(input.uuid);
    this._exp = input.exp;

    // params
    this.tags = input.tags;
    this.lastState = input.lastState;
    this.yielded = input.yielded;

    // Add service sessions
    assert(
      Array.isArray(input.contextIdentifiers),
      'Invalid contextIdentifiers: not a valid array.',
    );
    this.contextIdentifiers = input.contextIdentifiers;

    // Add data
    assert(
      Array.isArray(input.data),
      'Invalid data argument: not a valid array.',
    );
    for (const element of input.data) {
      assert(
        Array.isArray(element) && element.length === 2,
        'Invalid data[number] argument: not a valid array.',
      );
      this.setRawData(element[0], element[1]);
    }
  }

  /**
   * Get and set uuid
   */
  get uuid() {
    return this._uuid.clone();
  }
  set uuid(uuid: GamoraUUIDGuard) {
    assert(
      uuid instanceof GamoraUUIDGuard,
      'Invalid uuid: not a valid GamoraUUIDGuard.',
    );
    this._uuid = uuid.clone();
  }

  /**
   * Get and set context identifiers
   */
  get contextIdentifiers() {
    return this._contextIdentifiers.map(
      (contextIdentifier) => contextIdentifier,
    );
  }
  set contextIdentifiers(contextIdentifiers: string[]) {
    assert(
      Array.isArray(contextIdentifiers),
      'Invalid contextIdentifiers: not a valid array.',
    );
    assert(
      contextIdentifiers.every((contextIdentifier) => {
        return (
          typeof contextIdentifier === 'string' && contextIdentifier.length > 0
        );
      }),
      'Invalid contextIdentifiers: not a valid string array.',
    );
    this._contextIdentifiers = contextIdentifiers.map(
      (contextIdentifier) => contextIdentifier,
    );
  }

  /**
   * Get and set last state
   */
  get lastState(): HoruzProtocolChulakLastState | null {
    return this._lastState;
  }
  set lastState(lastState: HoruzProtocolChulakLastState | null) {
    assert(
      convertEnumToValues(HoruzProtocolChulakLastState).includes(lastState) ||
        lastState === null,
      'Invalid lastState argument: not a HoruzProtocolChulakLastState.',
    );
    this._lastState = lastState;
  }

  /**
   * Get and set tags
   */
  get tags(): HoruzProtocolChulakServiceSessionGuardTags {
    return structuredClone(
      this._tags,
    ) as HoruzProtocolChulakServiceSessionGuardTags;
  }
  set tags(tags: HoruzProtocolChulakServiceSessionGuardTags | null) {
    assert(
      typeof tags === 'object' || tags === null,
      'Invalid tags argument: not an object.',
    );
    this._tags = structuredClone(tags);
  }

  /**
   * Get and set exp
   */
  get exp() {
    return this._exp;
  }
  set exp(exp: number) {
    assert(
      Number.isSafeInteger(exp) && exp > 0,
      'Invalid exp: not a valid safe integer.',
    );
    this._exp = exp;
  }

  /**
   * Get and set yielded
   */
  get yielded(): HoruzProtocolChulakServiceStateYielder[] {
    return Array.from(this._yielded.values());
  }
  set yielded(yielded: HoruzProtocolChulakServiceStateYielder[]) {
    assert(
      yielded.every((state) =>
        convertEnumToValues(HoruzProtocolChulakServiceStateYielder).includes(
          state,
        ),
      ),
      'Invalid yielded argument: not all elements are HoruzProtocolChulakServiceStateYielder.',
    );

    this._yielded = new Set(yielded);
  }

  /**
   * Creates emtpy
   * @returns
   */
  static override Empty(): HoruzProtocolChulakServiceSessionGuard {
    return HoruzProtocolChulakServiceSessionGuard.fromJSON({
      uuid: GamoraUUIDGuard.Empty().toJSON(),
      exp: 0,
      contextIdentifiers: [],
      lastState: null,
      yielded: [],
      data: [],
      tags: null,
    } as HoruzProtocolChulakServiceSessionGuardInput);
  }

  /**
   * Gets from JSON
   * @param input - Input string
   */
  static override fromJSON(
    input: unknown,
  ): HoruzProtocolChulakServiceSessionGuard {
    assert(
      typeof input === 'object' && input !== null,
      'Invalid input argument: not an object.',
    );
    return new HoruzProtocolChulakServiceSessionGuard(
      input as HoruzProtocolChulakServiceSessionGuardInput,
    );
  }

  /**
   * Get from base64URL
   * @param input - The input
   */
  static override fromBase64URL(
    input: unknown,
  ): HoruzProtocolChulakServiceSessionGuard {
    assert(
      typeof input === 'string' && input.length > 0,
      'Invalid input argument: not a valid string.',
    );

    // Parse input
    let json;
    try {
      json = JSON.parse(GamoraBase64URLGuard.fromBase64URL(input).toUTF8());
    } catch {
      throw new GamoraErrorUnhandledException(
        'Unable to parse input: not valid JSON in base64url input.',
      );
    }

    // Create new guard
    return new HoruzProtocolChulakServiceSessionGuard(
      json as HoruzProtocolChulakServiceSessionGuardInput,
    );
  }

  /**
   * Gets JSON
   * @returns string
   */
  override toJSON(): HoruzProtocolChulakServiceSessionGuardInput {
    return {
      uuid: this.uuid.toJSON(),
      exp: this.exp,
      tags: this.tags,
      lastState: this.lastState,
      yielded: this.yielded,
      contextIdentifiers: this.contextIdentifiers,
      data: Array.from(this._data.entries()).map(([e, v]) => {
        return [e, Array.from(v)];
      }),
    };
  }

  /**
   * To Base64URL
   */
  override toBase64URL(): string {
    const json = this.toJSON();
    return GamoraBase64URLGuard.fromUTF8(JSON.stringify(json)).toBase64URL();
  }

  /**
   * Create clone
   */
  override clone() {
    return HoruzProtocolChulakServiceSessionGuard.fromJSON(this.toJSON());
  }

  /**
   * Gets data by type
   * @param type The data type
   * @returns
   */
  hasDataByType(type: HoruzProtocolChulakDataType) {
    assert(
      convertEnumToValues(HoruzProtocolChulakDataType).includes(type),
      'Invalid type argument: not a valid HoruzProtocolChulakDataType.',
    );
    return this._data.has(type);
  }

  /**
   * Expose mutable map
   */
  getData() {
    return this._data;
  }

  /**
   * Gets data by type
   * @param type The data type
   * @returns
   */
  getDataByType(type: HoruzProtocolChulakDataType): Uint8Array {
    assert(
      convertEnumToValues(HoruzProtocolChulakDataType).includes(type),
      'Invalid type argument: not a valid HoruzProtocolChulakDataType.',
    );
    assert(this._data.has(type), 'Invalid type: does not exist in data map.');
    return this._data.get(type) as Uint8Array;
  }

  /**
   * Adds data
   * @param type The data type to add
   * @param data The json input
   */
  setRawData(type: HoruzProtocolChulakDataType, input: number[]) {
    assert(
      convertEnumToValues(HoruzProtocolChulakDataType).includes(type),
      'Invalid type argument: not a valid HoruzProtocolChulakDataType.',
    );
    assert(Array.isArray(input), 'Invalid input argument: not a valid array.');
    assert(
      input.every((v) => {
        return Number.isSafeInteger(v) && v >= 0 && v < 256;
      }),
      'Invalid input[number] argument: not a valid array of numbers.',
    );
    this._data.set(type, new Uint8Array(input));
  }

  /**
   * Adds data
   * @param type The data type to add
   * @param data The json input
   */
  setData(type: HoruzProtocolChulakDataType, data: Uint8Array) {
    assert(
      convertEnumToValues(HoruzProtocolChulakDataType).includes(type),
      'Invalid type argument: not a valid HoruzProtocolChulakDataType.',
    );
    assert(
      data instanceof Uint8Array,
      'Invalid data argument: not a valid Uint8Array.',
    );
    this._data.set(type, data);
  }

  /**
   * Gets mutable service session uuids
   */
  getContextIdentifiers() {
    return this._contextIdentifiers;
  }

  /**
   * Gets mutable service session uuids
   */
  addContextIdentifier(contextIdentifier: GamoraControllerContextIdentifier) {
    assert(
      !this._contextIdentifiers.includes(contextIdentifier),
      'Invalid contextIdentifier argument: already exists in list.',
    );
    this._contextIdentifiers.push(contextIdentifier);
  }

  /**
   * Has an active context identifier
   * @returns Last context identifier
   */
  hasActiveContextIdentifier() {
    return this._contextIdentifiers.length !== 0;
  }

  /**
   * Gets the active context identifier
   * @returns Last context identifier
   */
  getActiveContextIdentifier() {
    assert(
      this._contextIdentifiers.length !== 0,
      'No active context identifier: context identifier list is empty',
    );
    return this._contextIdentifiers[this._contextIdentifiers.length - 1];
  }

  /**
   * Marks a yielder as yielded in this session
   * @param yielder The yielder to mark as yielded
   */
  addYielded(yielder: HoruzProtocolChulakServiceStateYielder) {
    assert(
      convertEnumToValues(HoruzProtocolChulakServiceStateYielder).includes(
        yielder,
      ),
      'Invalid yielder argument: not a are HoruzProtocolChulakServiceStateYielder.',
    );

    this._yielded.add(yielder);
  }

  /**
   * Checks if the given yielder has been yielded in this session
   * @param yielder The yielder to check if it has been yielded
   * @returns Whether the given yielder has been yielded
   */
  hasYielded(yielder: HoruzProtocolChulakServiceStateYielder) {
    assert(
      convertEnumToValues(HoruzProtocolChulakServiceStateYielder).includes(
        yielder,
      ),
      'Invalid yielder argument: not a are HoruzProtocolChulakServiceStateYielder.',
    );

    return this._yielded.has(yielder);
  }
}
